import type { BillingView } from "@mailhand/core/api";
import {
  MAX_BILL_LIMIT_USD,
  METERED_COUNTERS,
  type MeteredCounter,
  PAID_PLAN_IDS,
  PLANS,
  PLAN_IDS,
  type PaidPlanId,
  formatCount,
  formatMicrosUsd,
  formatUsd,
  isPaidPlanId,
  planIdSchema,
} from "@mailhand/core/billing";
import { type SubmitEvent, useEffect, useRef, useState } from "react";
import { navigate } from "../../app/router.js";
import { useApi } from "../../app/services.js";
import { useTenant } from "../../app/tenant.js";
import { useTask } from "../../app/useTask.js";
import { usePoll } from "../../lib/usePoll.js";
import { errorMessage } from "../../lib/useResource.js";
import { Button, Card, CardHeader, Field, Input, Select, useToast } from "../../ui/index.js";
import { periodLabel, planLines, planPrice, renewalLabel, subscriptionNote } from "./billingText.js";

const COUNTER_LABELS: Record<MeteredCounter, string> = { messages: "Emails", writerCalls: "Written replies" };

/** Stripe sends the member back with `?billing=success`; the webhook may land a few seconds later. */
const RETURN_POLL_MS = 2_000;
const RETURN_POLL_TRIES = 30;

export function BillingSection() {
  const { billing } = useTenant();
  useCheckoutReturn();
  const note = subscriptionNote(billing);
  return (
    <Card id="billing">
      <CardHeader
        eyebrow="Billing"
        title={billing.state === "paymentProblem" ? "Payment needed" : `${PLANS[billing.plan].name} plan`}
        aside={<span className="mono muted small">{periodLabel(billing)}</span>}
      />
      <div className="stack">
        {note === null ? null : <p className="note note-warn">{note}</p>}
        {METERED_COUNTERS.map((counter) => (
          <UsageMeter key={counter} label={COUNTER_LABELS[counter]} used={billing.usage[counter]} allowance={billing.allowance[counter]} />
        ))}
        <p className="muted small">
          {billing.plan === "free"
            ? `Usage starts again on ${renewalLabel(billing)}.`
            : `Estimated bill this month: ${formatMicrosUsd(billing.estimatedBillMicros, "cents")}. The plan renews on ${renewalLabel(billing)}.`}
        </p>
        {billing.plan === "free" ? null : <BillLimitForm key={billing.billLimitUsd} view={billing} plan={billing.plan} />}
        <hr className="rule" />
        {billing.state === "free" ? <PlanCards /> : null}
        {billing.plan === "free" ? null : <ChangePlan view={billing} plan={billing.plan} />}
        {billing.hasCustomer ? <ManageBilling /> : null}
      </div>
    </Card>
  );
}

function UsageMeter({ label, used, allowance }: { label: string; used: number; allowance: number }) {
  const share = used / allowance;
  return (
    <div className={`usage-meter${share >= 1 ? " is-full" : share >= 0.8 ? " is-high" : ""}`}>
      <div className="usage-meter-head">
        <span>{label}</span>
        <span className="mono muted">
          {formatCount(used)} / {formatCount(allowance)}
        </span>
      </div>
      <div className="usage-meter-track" role="meter" aria-label={label} aria-valuemin={0} aria-valuemax={allowance} aria-valuenow={used}>
        <span className="usage-meter-fill" style={{ width: `${String(Math.min(100, Math.round(share * 100)))}%` }} />
      </div>
    </div>
  );
}

function BillLimitForm({ view, plan }: { view: BillingView; plan: PaidPlanId }) {
  const api = useApi();
  const { setBilling } = useTenant();
  const task = useTask();
  const [value, setValue] = useState(String(view.billLimitUsd ?? ""));
  const [error, setError] = useState<string | undefined>(undefined);
  const price = PLANS[plan].priceUsd;

  async function submit(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    const billLimitUsd = Number(value);
    if (!Number.isInteger(billLimitUsd) || billLimitUsd < price || billLimitUsd > MAX_BILL_LIMIT_USD) {
      setError(`A whole number of dollars, from ${formatUsd(price)} to ${formatUsd(MAX_BILL_LIMIT_USD)}.`);
      return;
    }
    setError(undefined);
    await task.run(async () => { setBilling(await api.call("billingPatch", { body: { billLimitUsd } })); }, "Bill limit saved.");
  }

  return (
    <form className="inline-form" onSubmit={(event) => void submit(event)} noValidate>
      <Field label="Bill limit (USD per month)" hint="At this amount, new email is held until the next month." error={error}>
        {({ id, describedBy, invalid }) => (
          <Input id={id} className="mono" type="number" min={price} value={value} aria-describedby={describedBy} aria-invalid={invalid} onChange={(event) => { setValue(event.target.value); }} />
        )}
      </Field>
      <Button type="submit" busy={task.busy}>
        Save limit
      </Button>
    </form>
  );
}

function PlanCards() {
  const api = useApi();
  const task = useTask();
  const [chosen, setChosen] = useState<PaidPlanId | null>(null);

  async function upgrade(plan: PaidPlanId): Promise<void> {
    setChosen(plan);
    await task.run(async () => {
      const { url } = await api.call("billingCheckout", { body: { plan } });
      window.location.assign(url);
    });
  }

  return (
    <div className="plan-cards">
      {PAID_PLAN_IDS.map((planId) => {
        const plan = PLANS[planId];
        return (
          <div key={planId} className="subcard plan-card">
            <div className="plan-card-name">{plan.name}</div>
            <div className="plan-card-price mono">{planPrice(plan)}</div>
            <ul className="plan-card-lines small muted">
              {planLines(plan).map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
            <Button variant="primary" busy={task.busy && chosen === planId} disabled={task.busy} onClick={() => void upgrade(planId)}>
              Upgrade
            </Button>
          </div>
        );
      })}
    </div>
  );
}

function ChangePlan({ view, plan }: { view: BillingView; plan: PaidPlanId }) {
  const api = useApi();
  const { setBilling, reloadView } = useTenant();
  const task = useTask();
  const [target, setTarget] = useState<string>(plan);
  const unchanged = target === plan && !view.cancelAtPeriodEnd;

  async function change(): Promise<void> {
    const next = planIdSchema.parse(target);
    const message = isPaidPlanId(next) ? `You are on ${PLANS[next].name}.` : `Your plan ends on ${renewalLabel(view)}.`;
    await task.run(async () => {
      setBilling(await api.call("billingPlan", { body: { plan: next } }));
      // A plan change resets the daily caps, which the tenant view holds.
      await reloadView();
    }, message);
  }

  return (
    <div className="inline-form">
      <Field label="Plan" hint="A paid plan changes now, prorated. Free starts at the end of the month.">
        {({ id, describedBy }) => (
          <Select id={id} aria-describedby={describedBy} value={target} disabled={task.busy} onChange={(event) => { setTarget(event.target.value); }}>
            {PLAN_IDS.map((planId) => (
              <option key={planId} value={planId}>
                {PLANS[planId].name} · {planPrice(PLANS[planId])}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Button busy={task.busy} disabled={unchanged} onClick={() => void change()}>
        Change plan
      </Button>
    </div>
  );
}

function ManageBilling() {
  const api = useApi();
  const task = useTask();
  async function open(): Promise<void> {
    await task.run(async () => {
      const { url } = await api.call("billingPortal", {});
      window.location.assign(url);
    });
  }
  return (
    <div className="button-row">
      <Button variant="ghost" busy={task.busy} onClick={() => void open()}>
        Manage billing
      </Button>
      <span className="muted small">Card, invoices, and cancel.</span>
    </div>
  );
}

/** After Checkout, polls GET /billing until the plan changes, then clears `?billing=success`. */
function useCheckoutReturn(): void {
  const { billing, reloadBilling } = useTenant();
  const toast = useToast();
  const [returning, setReturning] = useState(() => new URLSearchParams(window.location.search).get("billing") === "success");
  const tries = useRef(0);

  useEffect(() => {
    if (!returning || billing.plan === "free") return;
    toast.show(`You are on ${PLANS[billing.plan].name}.`, "success");
    setReturning(false);
    navigate("/settings#billing", { replace: true });
  }, [returning, billing.plan, toast]);

  usePoll(
    async () => {
      tries.current += 1;
      if (tries.current > RETURN_POLL_TRIES) {
        setReturning(false);
        toast.show("Stripe has not confirmed the plan yet. Reload this page in a minute.", "error");
        return;
      }
      try {
        await reloadBilling();
      } catch (error) {
        setReturning(false);
        toast.show(errorMessage(error), "error");
      }
    },
    RETURN_POLL_MS,
    returning && billing.plan === "free",
    true,
  );
}
